import React, { createContext, useContext, useState, useCallback, useRef } from "react";

const PrescriptionHoverContext = createContext(null);

export const PrescriptionHoverProvider = ({ children }) => {
  const [hoveredCodes, setHoveredCodes] = useState(new Set());
  const codeElementsRef = useRef(new Map());
  const polygonElementsRef = useRef(new Map());

  const registerCodeElement = useCallback((codeValue, element) => {
    if (element) {
      if (!codeElementsRef.current.has(codeValue)) {
        codeElementsRef.current.set(codeValue, new Set());
      }
      codeElementsRef.current.get(codeValue).add(element);
    }
    return () => {
      if (codeElementsRef.current.has(codeValue)) {
        const elements = codeElementsRef.current.get(codeValue);
        elements.delete(element);
        if (elements.size === 0) {
          codeElementsRef.current.delete(codeValue);
        }
      }
    };
  }, []);

  const registerPolygonElement = useCallback((codeValue, element) => {
    if (element) {
      if (!polygonElementsRef.current.has(codeValue)) {
        polygonElementsRef.current.set(codeValue, new Set());
      }
      polygonElementsRef.current.get(codeValue).add(element);
    }
    return () => {
      if (polygonElementsRef.current.has(codeValue)) {
        const elements = polygonElementsRef.current.get(codeValue);
        elements.delete(element);
        if (elements.size === 0) {
          polygonElementsRef.current.delete(codeValue);
        }
      }
    };
  }, []);

  const onCodeHover = useCallback((codeValue) => {
    setHoveredCodes((prev) => new Set([...prev, codeValue]));

    if (polygonElementsRef.current.has(codeValue)) {
      const elements = polygonElementsRef.current.get(codeValue);
      elements.forEach((element) => {
        element.style.setProperty("fill", element.classList.contains("fill-[url(#action-removed-stripes)]") ? "url(#action-removed-stripes-hover)" : "blue");
      });
    }
  }, []);

  const onCodeUnhover = useCallback((codeValue) => {
    setHoveredCodes((prev) => {
      const newSet = new Set(prev);
      newSet.delete(codeValue);
      return newSet;
    });

    if (polygonElementsRef.current.has(codeValue)) {
      const elements = polygonElementsRef.current.get(codeValue);
      elements.forEach((element) => {
        element.style.removeProperty("fill");
      });
    }
  }, []);

  const onPolygonHover = useCallback((codes) => {
    setHoveredCodes((prev) => new Set([...prev, ...codes]));

    for (const code of codes) {
      if (codeElementsRef.current.has(code)) {
        const elements = codeElementsRef.current.get(code);
        elements.forEach((element) => {
          element.style.setProperty("border", "1px solid blue");
        });
      }
    }
  }, []);

  const onPolygonUnhover = useCallback((codes) => {
    setHoveredCodes((prev) => {
      const newSet = new Set(prev);
      for (const code of codes) {
        newSet.delete(code);
      }
      return newSet;
    });

    for (const code of codes) {
      if (codeElementsRef.current.has(code)) {
        const elements = codeElementsRef.current.get(code);
        elements.forEach((element) => {
          element.style.removeProperty("border");
        });
      }
    }
  }, []);

  const value = {
    hoveredCodes,
    onCodeHover,
    onCodeUnhover,
    onPolygonHover,
    onPolygonUnhover,
    registerCodeElement,
    registerPolygonElement,
  };

  return <PrescriptionHoverContext.Provider value={value}>{children}</PrescriptionHoverContext.Provider>;
};

export const usePrescriptionHover = () => {
  const context = useContext(PrescriptionHoverContext);
  if (!context) {
    throw new Error("usePrescriptionHover must be used within PrescriptionHoverProvider");
  }
  return context;
};
